import { LoaderFunctionArgs, redirect } from "react-router-dom";

import { queryClient } from "@shared/config";
import {
  getMeModules,
  accessibleStructuresQueryOptions,
  getOperationalModuleSlug,
  type MeModule,
} from "@shared/api/server/gs3Server";

// Loader compartilhado da plataforma de acesso operacional (fatia 3).
// Fonte: docs/prd/operational-modules/operational-modules.md §3.3.2 / §3.3.3;
// quality-test §0.3.2. Bloqueia deep links para módulos não publicados
// (isReleased=false) ou sem autorização (hasAccess=false) ANTES de montar a UI,
// e guarda o contexto de estrutura (/<slug>/:structureId).

const ME_MODULES_QUERY = {
  queryKey: ["me", "modules"] as const,
  queryFn: getMeModules,
};

const is401 = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | undefined)?.response
    ?.status === 401;

const selectorPathOf = (moduleKey: string): string => {
  const slug = getOperationalModuleSlug(moduleKey);
  return slug ? `/${slug}` : "/";
};

// Gate do módulo: o usuário só monta a árvore do módulo se ele estiver publicado
// E autorizado. Catálogo vem de GET /v1/user/me/modules (cache da sessão). 401 =
// sessão inválida → /login; qualquer outra falha → Home (default seguro).
export const operationalModuleLoader =
  (moduleKey: string) =>
  async (_args: LoaderFunctionArgs): Promise<null> => {
    let modules: MeModule[];
    try {
      modules = await queryClient.ensureQueryData<MeModule[]>(ME_MODULES_QUERY);
    } catch (error) {
      if (is401(error)) throw redirect("/login");
      throw redirect("/");
    }

    const mod = modules.find((m) => m.module === moduleKey);
    if (!mod || !mod.isReleased || !mod.hasAccess) {
      throw redirect("/");
    }

    return null;
  };

// Guard de estrutura: acesso direto a /<slug>/:structureId só prossegue quando a
// estrutura está entre as acessíveis do módulo; caso contrário volta ao seletor
// /<slug>. 401 → /login.
export const operationalStructureLoader =
  (moduleKey: string) =>
  async ({ params }: LoaderFunctionArgs): Promise<null> => {
    const selectorPath = selectorPathOf(moduleKey);

    let structures;
    try {
      structures = await queryClient.ensureQueryData(
        accessibleStructuresQueryOptions(moduleKey),
      );
    } catch (error) {
      if (is401(error)) throw redirect("/login");
      throw redirect(selectorPath);
    }

    const isAccessible = structures.some(
      (structure) => structure.structureId === params.structureId,
    );
    if (!isAccessible) {
      throw redirect(selectorPath);
    }

    return null;
  };
